import React, { useState, useEffect, useRef, useCallback } from "react";
import {
    Layout, Table, Input, Button, Form, Space, Row, Col,
    Breadcrumb, Modal, message, Typography, Tooltip, Switch
} from "antd";
import Headerdiv from "../../layout/header";
import Footerdiv from "../../layout/footer";
import {
    PlusOutlined, EditOutlined, HomeOutlined, GlobalOutlined,
    SearchOutlined, CheckCircleFilled, CloseCircleFilled, EnvironmentOutlined
} from "@ant-design/icons";
import api from "../../../services/axiosInstance";
import Highlighter from "react-highlight-words";
import "../../../styles/fluidDesign.css";

const { Content } = Layout;

const ManageLand = () => {
    const [lands, setLands] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [currentItem, setCurrentItem] = useState(null);
    const [addForm] = Form.useForm();
    const [editForm] = Form.useForm();

    const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
    const [isAddModalVisible, setIsAddModalVisible] = useState(false);

    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef(null);

    const handleSearch = (selectedKeys, confirm, dataIndex) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };
    const handleReset = (clearFilters) => {
        clearFilters();
        setSearchText('');
    };

    const getColumnSearchProps = (dataIndex) => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button type="primary" onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
                        icon={<SearchOutlined />} size="small" style={{ width: 90 }}>Search</Button>
                    <Button onClick={() => clearFilters && handleReset(clearFilters)} size="small" style={{ width: 90 }}>Reset</Button>
                    <Button type="link" size="small" onClick={() => { confirm({ closeDropdown: false }); setSearchText(selectedKeys[0]); setSearchedColumn(dataIndex); }}>Filter</Button>
                    <Button type="link" size="small" onClick={() => close()}>close</Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />,
        onFilter: (value, record) => record[dataIndex]?.toString().toLowerCase().includes(value.toLowerCase()),
        filterDropdownProps: { onOpenChange(open) { if (open) setTimeout(() => searchInput.current?.select(), 100); } },
        render: (text) => searchedColumn === dataIndex ? (
            <Highlighter
                highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                searchWords={[searchText]} autoEscape
                textToHighlight={text ? text.toString() : ''}
            />
        ) : text,
    });

    const fetchLands = useCallback(async (page = 1, pageSize = 10) => {
        try {
            setLoading(true);
            const res = await api.get(`/farmSetUp/land/list.php?page=${page}&limit=${pageSize}`);
            setLands(res.data.dataset.map(item => ({
                landId: item.land_id,
                landCode: item.land_code,
                landName: item.land_name,
                landCountry: item.land_country,
                landMapLong: item.land_map_long,
                landMapLat: item.land_map_lat,
                landMapLink: item.land_map_link,
                landActive: item.land_active,
            })));
            setPagination({
                current: res.data.pagination?.current_page || 1,
                pageSize: res.data.pagination?.per_page || 10,
                total: res.data.pagination?.total_records || 0,
            });
        } catch {
            message.error("Failed to fetch lands.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchLands(1, 10); }, [fetchLands]);
    const handleTableChange = (pg) => fetchLands(pg.current, pg.pageSize);

    const handleAdd = async (values) => {
        try {
            setLoading(true);
            const res = await api.post('/farmSetUp/land/add.php', {
                land_code: values.landCode,
                land_name: values.landName,
                land_country: values.landCountry,
                land_map_long: values.landMapLong,
                land_map_lat: values.landMapLat,
                land_map_link: values.landMapLink,
            });
            if (res.data.status === 'success') {
                message.success(res.data.message || "Land added successfully.");
                setIsAddModalVisible(false);
                fetchLands(pagination.current, pagination.pageSize);
                addForm.resetFields();
            } else throw new Error(res.data.message);
        } catch (err) {
            message.error(err?.response?.data?.message || "Failed to add land.");
        } finally { setLoading(false); }
    };

    const openEdit = (record) => {
        setCurrentItem(record);
        editForm.setFieldsValue({
            landCode: record.landCode,
            landName: record.landName,
            landCountry: record.landCountry,
            landMapLong: record.landMapLong,
            landMapLat: record.landMapLat,
            landMapLink: record.landMapLink,
        });
        setIsModalVisible(true);
    };

    const handleEdit = async (values) => {
        try {
            setLoading(true);
            const res = await api.post('/farmSetUp/land/edit.php', {
                land_id: currentItem.landId,
                land_code: values.landCode,
                land_name: values.landName,
                land_country: values.landCountry,
                land_map_long: values.landMapLong,
                land_map_lat: values.landMapLat,
                land_map_link: values.landMapLink,
            });
            if (res.data.status === 'success') {
                message.success(res.data.message || "Land updated successfully.");
                setIsModalVisible(false);
                fetchLands(pagination.current, pagination.pageSize);
                editForm.resetFields();
            } else throw new Error(res.data.message);
        } catch (err) {
            message.error(err?.response?.data?.message || "Failed to update land.");
        } finally { setLoading(false); }
    };

    const handleToggle = async (record, checked) => {
        try {
            const res = await api.post('/farmSetUp/land/toggle.php', {
                land_id: record.landId,
                land_active: checked ? 1 : 0,
            });
            if (res.data.status === 'success') {
                message.success(res.data.message);
                fetchLands(pagination.current, pagination.pageSize);
            } else throw new Error(res.data.message);
        } catch (err) {
            message.error(err?.response?.data?.message || "Failed to update status.");
        }
    };

    const columns = [
        {
            title: 'Code', dataIndex: 'landCode', key: 'landCode', width: 100,
            ...getColumnSearchProps('landCode'),
        },
        {
            title: 'Land Name', dataIndex: 'landName', key: 'landName',
            sorter: (a, b) => a.landName.localeCompare(b.landName),
            ...getColumnSearchProps('landName'),
        },
        {
            title: 'Country', dataIndex: 'landCountry', key: 'landCountry',
            ...getColumnSearchProps('landCountry'),
        },
        {
            title: 'Status', dataIndex: 'landActive', key: 'landActive',
            align: 'center', width: 100,
            render: (val) => val == 1
                ? <Tooltip title="Active"><Space size={4}><CheckCircleFilled style={{ color: 'green', fontSize: 16 }} /><Typography.Text style={{ color: 'green' }}>Active</Typography.Text></Space></Tooltip>
                : <Tooltip title="Inactive"><Space size={4}><CloseCircleFilled style={{ color: 'red', fontSize: 16 }} /><Typography.Text style={{ color: 'red' }}>Inactive</Typography.Text></Space></Tooltip>,
        },
        {
            title: 'Action', key: 'action', align: 'center', fixed: 'right', width: 120,
            render: (_, record) => (
                <Space>
                    <Tooltip title="Edit Land">
                        <Button type="text" icon={<EditOutlined />} onClick={() => openEdit(record)} />
                    </Tooltip>
                    <Tooltip title={record.landActive == 1 ? "Deactivate" : "Activate"}>
                        <Switch
                            size="small"
                            checked={record.landActive == 1}
                            onChange={(checked) => handleToggle(record, checked)}
                        />
                    </Tooltip>
                </Space>
            ),
        },
    ];

    return (
        <Layout className="fluid-page">
            <Headerdiv />

            <div className="fluid-page-header">
                <div className="floating-shape shape-1"></div>
                <div className="floating-shape shape-2"></div>
                <Row align="bottom" justify="space-between">
                    <Col>
                                                    <Breadcrumb
                                className="fluid-breadcrumb"
                                items={[
                                    { href: '/dash', title: <HomeOutlined /> },
                                    { href: '/farmoverview', title: 'Farm Overview' },
                                    { title: 'Manage Lands' },
                                ]}
                            />
                        <h1 className="fluid-page-title">
                            <GlobalOutlined /> Manage Lands
                        </h1>
                    </Col>
                    <Col xs={0} md={12} style={{ textAlign: 'right' }}>
                        <p className="fluid-page-subtitle">Configure AgroLand and geographic locations</p>
                    </Col>
                </Row>
            </div>

            <Content className="fluid-content">
                <div className="fluid-card">
                    <div className="fluid-card-header">
                        <h3 className="fluid-card-title">Land Inventory</h3>
                        <Space>
                            <Typography.Text type="secondary">{pagination.total} records</Typography.Text>
                            <Button type="primary" icon={<PlusOutlined />} onClick={() => setIsAddModalVisible(true)}>
                                Add New Land
                            </Button>
                        </Space>
                    </div>
                    <div className="fluid-table">
                        <Table
                            size="small"
                            columns={columns}
                            dataSource={lands}
                            rowKey="landId"
                            pagination={{
                                current: pagination.current,
                                pageSize: pagination.pageSize,
                                total: pagination.total,
                                showSizeChanger: true,
                            }}
                            onChange={handleTableChange}
                            loading={loading}
                            scroll={{ x: 800 }}
                        />
                    </div>
                </div>
            </Content>

            <Footerdiv />

            <Modal
                title="Add New Land"
                open={isAddModalVisible}
                onCancel={() => { setIsAddModalVisible(false); addForm.resetFields(); }}
                footer={null}
                width={700}
                destroyOnClose
            >
                <Form form={addForm} layout="vertical" onFinish={handleAdd}>
                    <Row gutter={[16, 0]}>
                        <Col xs={24} md={4}>
                            <Form.Item label="Code" name="landCode" rules={[{ required: true, message: 'Code required' }]}>
                                <Input placeholder="L001" maxLength={5} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={10}>
                            <Form.Item label="Land Name" name="landName" rules={[{ required: true, message: 'Name required' }]}>
                                <Input placeholder="Main Farm South" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={10}>
                            <Form.Item label="Country" name="landCountry">
                                <Input placeholder="Sri Lanka" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={[16, 0]}>
                        <Col xs={24} md={6}>
                            <Form.Item label="Longitude" name="landMapLong">
                                <Input placeholder="80.1234" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={6}>
                            <Form.Item label="Latitude" name="landMapLat">
                                <Input placeholder="7.1234" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item label="Map Link" name="landMapLink">
                                <Input placeholder="https://maps.google.com/..." />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row justify="end">
                        <Form.Item style={{ marginBottom: 0 }}>
                            <Space>
                                <Button danger onClick={() => { setIsAddModalVisible(false); addForm.resetFields(); }}>Cancel</Button>
                                <Button type="primary" htmlType="submit" icon={<PlusOutlined />} loading={loading}>Add Land</Button>
                            </Space>
                        </Form.Item>
                    </Row>
                </Form>
            </Modal>

            <Modal
                title="Edit Land Details"
                open={isModalVisible}
                onCancel={() => { setIsModalVisible(false); editForm.resetFields(); }}
                footer={null}
                width={700}
            >
                <Form form={editForm} onFinish={handleEdit} layout="vertical">
                    <Row gutter={[16, 0]}>
                        <Col span={8}>
                            <Form.Item label="Code" name="landCode" rules={[{ required: true }]}>
                                <Input maxLength={5} />
                            </Form.Item>
                        </Col>
                        <Col span={16}>
                            <Form.Item label="Land Name" name="landName" rules={[{ required: true }]}>
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item label="Country" name="landCountry">
                        <Input />
                    </Form.Item>
                    <Row gutter={[16, 0]}>
                        <Col span={12}>
                            <Form.Item label="Longitude" name="landMapLong">
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="Latitude" name="landMapLat">
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item label="Map Link" name="landMapLink">
                        <Input />
                    </Form.Item>
                    <Form.Item>
                        <Space>
                            <Button type="primary" htmlType="submit" loading={loading}>Save Changes</Button>
                            <Button danger onClick={() => setIsModalVisible(false)}>Cancel</Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </Layout>
    );
};

export default ManageLand;
